"use client"

import { useState } from "react"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"
import { Calculator, TrendingDown, Users, FileText, Key } from "lucide-react"
import Link from "next/link"

export function MiniCalculator() {
  const [reviewers, setReviewers] = useState([5])
  const [words, setWords] = useState([50000])
  const [selectedPlatform, setSelectedPlatform] = useState<'lokalise' | 'crowdin'>('crowdin')

  // Simplified pricing calculations
  const calculateLocsyPrice = () => {
    const platformFee = 20
    const openAiCost = Math.ceil(words[0] / 1000) * 0.01
    return platformFee + openAiCost
  }

  const calculateCompetitorPrice = () => {
    if (selectedPlatform === 'crowdin') {
      const basePrice = 150
      const additionalUsers = Math.max(0, reviewers[0] - 5)
      const userCost = additionalUsers * 10
      const wordTier = words[0] > 100000 ? 100 : words[0] > 50000 ? 50 : 0
      return basePrice + userCost + wordTier
    } else {
      const basePrice = 140
      const additionalUsers = Math.max(0, reviewers[0] - 10)
      const userCost = additionalUsers * 14
      return basePrice + userCost
    }
  }

  const locsyPrice = calculateLocsyPrice()
  const competitorPrice = calculateCompetitorPrice()
  const savings = competitorPrice - locsyPrice

  return (
    <div className="py-16 bg-muted/30 rounded-3xl">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <Badge variant="secondary" className="mb-4">
              <Calculator className="w-3 h-3 mr-1" />
              Quick Savings Calculator
            </Badge>
            <h2 className="text-2xl font-bold mb-4">
              See Your Potential Savings
            </h2>
            <p className="text-muted-foreground">
              Compare your costs with enterprise platforms instantly
            </p>
          </div>

          {/* Platform Toggle */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center rounded-lg bg-muted p-1">
              <button
                onClick={() => setSelectedPlatform('lokalise')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                  selectedPlatform === 'lokalise'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                vs Lokalise
              </button>
              <button
                onClick={() => setSelectedPlatform('crowdin')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-all ${
                  selectedPlatform === 'crowdin'
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                vs Crowdin
              </button>
            </div>
          </div>

          {/* Sliders */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center">
                  <Users className="h-4 w-4 mr-2" />
                  Team Size: {reviewers[0]}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Slider
                  value={reviewers}
                  onValueChange={setReviewers}
                  max={25}
                  min={1}
                  step={1}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>1</span>
                  <span>25</span>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center">
                  <FileText className="h-4 w-4 mr-2" />
                  Words: {words[0].toLocaleString()}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Slider
                  value={words}
                  onValueChange={setWords}
                  max={100000}
                  min={1000}
                  step={1000}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-muted-foreground mt-2">
                  <span>1K</span>
                  <span>100K</span>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Results */}
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <Card className="border-2 border-primary">
              <CardHeader>
                <CardTitle className="text-lg">Locsy</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-primary mb-2">
                  ${locsyPrice.toFixed(0)}/month
                </div>
                <p className="text-sm text-muted-foreground">
                  $20 platform + ${(locsyPrice - 20).toFixed(0)} OpenAI costs
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {selectedPlatform === 'lokalise' ? 'Lokalise' : 'Crowdin'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-600 mb-2">
                  ${competitorPrice.toFixed(0)}/month
                </div>
                <p className="text-sm text-muted-foreground">
                  Base plan + user fees + usage tiers
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Savings */}
          <Card className="border-2 border-green-500 mb-6">
            <CardContent className="p-6 text-center">
              <div className="flex items-center justify-center mb-2">
                <TrendingDown className="h-5 w-5 text-green-600 mr-2" />
                <span className="text-green-600 font-semibold text-lg">
                  Save ${savings.toFixed(0)}/month
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                Annual savings: ${(savings * 12).toFixed(0)} • 
                {((savings / competitorPrice) * 100).toFixed(0)}% cost reduction
              </p>
            </CardContent>
          </Card>

          {/* CTA */}
          <div className="text-center">
            <Button size="lg" className="mr-4" asChild>
              <Link href="/calculator">See Full Calculator</Link>
            </Button>
            <Button variant="outline" size="lg" asChild>
              <Link href="/auth/signup">Start Free Trial</Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">
              Get detailed pricing breakdown and compare all platforms
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}